import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Divider,
  Paper,
  Stack,
  Alert,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import StatusChip from "../components/StatusChip";
import useVendorStore from "../store/useVendorStore";
import { styles } from "../styles/Vendors.styles";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const TYPE_META = {
  Vulnerability: { icon: BugReportOutlinedIcon, colorKey: "error" },
  "Posture Change": { icon: TimelineOutlinedIcon, colorKey: "warning" },
  Certification: { icon: VerifiedOutlinedIcon, colorKey: "success" },
  News: { icon: NewspaperOutlinedIcon, colorKey: "info" },
};

const FILTERS = [
  { id: "open", label: "Open" },
  { id: "acknowledged", label: "Acknowledged" },
  { id: "all", label: "All" },
];

const MonitoringFeed = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const monitoring = useVendorStore((s) => s.monitoring);
  const acknowledgeAlert = useVendorStore((s) => s.acknowledgeAlert);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [filter, setFilter] = useState("open");

  const openCount = monitoring.filter((m) => !m.acknowledged).length;
  const highCount = monitoring.filter(
    (m) => !m.acknowledged && (m.severity === "High" || m.severity === "Critical")
  ).length;
  const ackCount = monitoring.filter((m) => m.acknowledged).length;

  const sorted = useMemo(() => {
    const list = [...monitoring].sort((a, b) => {
      if (a.acknowledged !== b.acknowledged) return a.acknowledged ? 1 : -1;
      return new Date(b.date) - new Date(a.date);
    });
    if (filter === "open") return list.filter((m) => !m.acknowledged);
    if (filter === "acknowledged") return list.filter((m) => m.acknowledged);
    return list;
  }, [monitoring, filter]);

  const handleAck = (alert) => {
    acknowledgeAlert(alert.id);
    setSnackbarInfo({ message: "Alert acknowledged" });
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />

      <Alert
        severity="info"
        sx={{
          mb: 2,
          backgroundColor: theme.customColors.background.info,
          border: `1px solid ${theme.customColors.border.accent}`,
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ color: theme.customColors.text.strong, mb: 0.5 }}>
          Continuous vendor monitoring
        </Typography>
        <Typography variant="body2">
          Alerts are generated from certifications, vulnerabilities, and posture changes across your vendor portfolio.
        </Typography>
      </Alert>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <Paper elevation={0} sx={styles.statsCard}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
                {openCount}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                Open Alerts
              </Typography>
            </Box>
            <NotificationsActiveIcon sx={{ color: theme.customColors.icons.primary, fontSize: 24 }} />
          </Box>
        </Paper>
        <Paper elevation={0} sx={styles.statsCard}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color={theme.customColors.status.error.text}>
                {highCount}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                High Severity
              </Typography>
            </Box>
            <BugReportOutlinedIcon sx={{ color: theme.customColors.status.error.main, fontSize: 24 }} />
          </Box>
        </Paper>
        <Paper elevation={0} sx={styles.statsCard}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color={theme.customColors.status.success.text}>
                {ackCount}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                Acknowledged
              </Typography>
            </Box>
            <CheckCircleOutlineIcon sx={{ color: theme.customColors.status.success.main, fontSize: 24 }} />
          </Box>
        </Paper>
      </Stack>

      <Card sx={styles.mainCard} variant="outlined">
        <CardHeader
          title={
            <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
              Monitoring Feed
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1} sx={{ pt: 0.5, pr: 1 }}>
              {FILTERS.map((item) => {
                const selected = filter === item.id;
                return (
                  <Button
                    key={item.id}
                    size="small"
                    variant={selected ? "contained" : "outlined"}
                    onClick={() => setFilter(item.id)}
                    sx={
                      selected
                        ? { ...styles.addButton, px: 1.5, py: 0.5 }
                        : {
                            textTransform: "none",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.5,
                            borderColor: theme.customColors.border.main,
                            color: theme.customColors.text.primary,
                          }
                    }
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          }
          sx={styles.cardHeader}
        />
        <CardContent sx={{ p: 0 }}>
          {sorted.length === 0 ? (
            <Box sx={styles.emptyState}>
              <NotificationsNoneIcon
                sx={{ fontSize: 48, color: theme.customColors.icons.disabled, mb: 3 }}
              />
              <Typography variant="h5" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                {filter === "open" ? "No open alerts" : "No monitoring alerts"}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400, lineHeight: 1.5 }}>
                {filter === "open"
                  ? "You're caught up. Acknowledged alerts are available in the Acknowledged filter."
                  : "New vendor alerts will appear here as certifications, vulnerabilities, and posture changes are detected."}
              </Typography>
            </Box>
          ) : (
            sorted.map((alert, idx) => {
              const typeMeta = TYPE_META[alert.type] || TYPE_META.News;
              const TypeIcon = typeMeta.icon;
              const tone = theme.customColors.status[typeMeta.colorKey] || theme.customColors.status.info;

              return (
                <React.Fragment key={alert.id}>
                  {idx > 0 && <Divider sx={{ borderColor: theme.customColors.border.main }} />}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 2.5,
                      backgroundColor: alert.acknowledged
                        ? theme.customColors.background.header
                        : theme.customColors.background.main,
                      borderLeft: alert.acknowledged
                        ? "3px solid transparent"
                        : `3px solid ${tone.main}`,
                      "&:hover": {
                        backgroundColor: theme.customColors.background.hover,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        backgroundColor: tone.bg,
                        color: tone.text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <TypeIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                        <Typography variant="body1" fontWeight={600} color={theme.customColors.text.strong}>
                          {alert.title}
                        </Typography>
                        <StatusChip label={alert.severity} />
                        <StatusChip label={alert.type} />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1, color: theme.customColors.text.primary }}>
                        {alert.description}
                      </Typography>
                      <Typography
                        variant="body2"
                        onClick={() => navigate(`/admin/vendors/${alert.vendorId}`)}
                        sx={{
                          color: theme.customColors.button.primary,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {alert.vendorName}
                      </Typography>
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        {formatDate(alert.date)}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                      {alert.acknowledged ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: "8px",
                            backgroundColor: theme.customColors.status.success.bg,
                          }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: 16, color: theme.customColors.status.success.text }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={theme.customColors.status.success.text}
                          >
                            Acknowledged
                          </Typography>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAck(alert)}
                          sx={{
                            ...styles.addButton,
                            px: 1.5,
                            py: 0.5,
                            minWidth: "auto",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Acknowledge
                        </Button>
                      )}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default MonitoringFeed;
